import { Button } from '@jouzen/ecom-components/v3/Button';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import { EventType } from '@/analytics/types';
import MembershipSelection from '@/app/[locale]/product/_steps/enums/MembershipSelection';
import WarrantySelection from '@/app/[locale]/product/_steps/enums/WarrantySelection';

import type { Order } from '../_types/order';

interface ModalFooterProps {
  readonly isAddingToCart: boolean;
  readonly onAddToCart: () => Promise<void>;
  readonly order: Order;
  readonly proceedButtonText: MessageKey;
}

const ModalFooter = ({
  isAddingToCart,
  onAddToCart,
  order,
  proceedButtonText,
}: ModalFooterProps) => {
  const t = useTranslations();

  const selectedWarranty = order.warranty.value as WarrantySelection;
  const selectedMembership = order.membership.value as MembershipSelection;
  const isSkipButton = proceedButtonText === 'bundle_modal_skip_button';
  // analytics tracking for add to cart button on bundle modal
  // calls onAddToCart function in useAddOrderToCart hook
  const handleAddToCart = async (): Promise<void> => {
    if (
      selectedMembership === MembershipSelection.OneMonth &&
      selectedWarranty === WarrantySelection.OneYear
    ) {
      await window.ouraAnalytics.track(EventType.CTAClicked, {
        action: 'cart',
        cta: 'skip_this_step',
        location: 'add_on_module',
        path: '/cart',
      });
    } else {
      await window.ouraAnalytics.track(EventType.CTAClicked, {
        action: 'cart',
        cta: 'add_to_cart',
        location: 'add_on_module',
        path: '/cart',
      });
    }
    await onAddToCart();
  };

  return (
    <div
      className="sticky bottom-0 flex border-t border-gray-300 bg-sandstone-200 pt-4 pr-2 pb-2 md:justify-end"
      data-cy="bundle-fixed-footer"
    >
      <Button
        className={cx('w-full border-2 whitespace-nowrap md:w-auto', {
          'border-gray-300 bg-transparent md:hover:bg-sandstone-500/8':
            isSkipButton,
        })}
        data-cy={`bundle_${proceedButtonText}`}
        loading={isAddingToCart}
        loadingTextEnabled
        onClick={handleAddToCart}
        size="large"
        type="submit"
        variant="secondary-dark"
      >
        <Typography
          color={isSkipButton ? 'dark' : 'light'}
          Element="span"
          weight="black"
        >
          {t(proceedButtonText)}
        </Typography>
      </Button>
    </div>
  );
};

export default ModalFooter;
